import { call, put, takeLatest } from 'redux-saga/effects';
import { PayloadAction } from '@reduxjs/toolkit';
import { authApi } from '../../services/api';
import { brandingService } from '../../services/branding';
import { getTenantFromHostname } from '../../utils';
import { auth } from '../../firebase';
import { BrandingApiResponse } from '../../theme/types';
import {
  userAuthenticated,
  backendAuthSuccess,
  backendAuthFailure,
  fetchBrandingRequest,
  fetchBrandingSuccess,
  fetchBrandingFailure,
} from '../slices/brandingSlice';

interface UserAuthPayload {
  uid: string;
  email: string | null;
  displayName: string | null;
}

/**
 * Worker saga: Register user with backend and fetch branding
 * Triggered when user authenticates via Firebase
 */
function* handleUserAuthenticated(action: PayloadAction<UserAuthPayload>): Generator<any, void, any> {
  const { uid, email, displayName } = action.payload;
  const tenant = getTenantFromHostname();

  try {
    // Get fresh token from Firebase
    const token: string | null = yield call(async () => {
      if (!auth?.currentUser) return null;
      return auth.currentUser.getIdToken();
    });

    if (!token) {
      yield put(backendAuthFailure('No authentication token available'));
      return;
    }

    // Register user with backend
    yield call(authApi.register, {
      firebaseUid: uid,
      email,
      displayName,
      provider: 'google',
      tenant: tenant || 'default',
      token,
    });

    console.log('[BrandingSaga] User registered with backend:', { tenant: tenant || 'default' });
    yield put(backendAuthSuccess());

    // Now fetch branding
    yield put(fetchBrandingRequest());
  } catch (error: any) {
    const errorMessage = error?.response?.data?.message || error?.message || 'Backend registration failed';
    console.warn('[BrandingSaga] Backend auth failed:', errorMessage);
    
    // Even if registration fails, try to fetch branding
    yield put(backendAuthSuccess());
    yield put(fetchBrandingRequest());
  }
}

/**
 * Worker saga: Fetch branding configuration from API
 */
function* handleFetchBranding(): Generator<any, void, any> {
  try {
    const apiConfig: BrandingApiResponse | null = yield call(brandingService.fetchBrandingConfig);
    yield put(fetchBrandingSuccess(apiConfig));
  } catch (error: any) {
    const errorMessage = error?.message || 'Failed to fetch branding';
    console.warn('[BrandingSaga] Fetch branding failed:', errorMessage);
    yield put(fetchBrandingFailure(errorMessage));
  }
}

/**
 * Watcher saga: watches for branding-related actions
 */
function* watchBrandingActions() {
  yield takeLatest(userAuthenticated.type, handleUserAuthenticated);
  yield takeLatest(fetchBrandingRequest.type, handleFetchBranding);
}

export default function* brandingSaga() {
  yield watchBrandingActions();
}
